<template>
  <NcrmRecordEditDialog
    :model-value="modelValue"
    :title="resolvedTitle"
    :subtitle="resolvedSubtitle"
    :header-icon="resolvedHeaderIcon"
    :loading="submitting"
    :save-disabled="!canSubmit"
    :save-label="$t('ncrm.contacts.details.addToBoard.actions.submit')"
    :max-width="480"
    @update:model-value="handleModelUpdate"
    @save="submitSelection"
  >
    <v-progress-linear v-if="boardsLoading" indeterminate color="primary" class="mb-3" />
    <div class="d-flex flex-column ga-3">
      <v-select
        v-model="selectedBoardId"
        :items="boardOptions"
        :label="$t('ncrm.contacts.details.addToBoard.fields.board')"
        :loading="boardsLoading"
        :no-data-text="$t('ncrm.contacts.details.addToBoard.empty.boards')"
        :menu-props="selectMenuProps"
        hide-details="auto"
      />

      <v-select
        v-model="selectedStageId"
        :items="stageOptions"
        :label="$t('ncrm.contacts.details.addToBoard.fields.stage')"
        :loading="stagesLoading"
        :disabled="!selectedBoardId"
        :no-data-text="$t('ncrm.contacts.details.addToBoard.empty.stages')"
        :menu-props="selectMenuProps"
        hide-details="auto"
      />

      <v-text-field
        v-if="mode === 'deal'"
        v-model="dealName"
        :label="$t('ncrm.contacts.details.addToBoard.fields.dealName')"
        :counter="190"
        :maxlength="190"
        hide-details="auto"
      />

      <v-autocomplete
        v-model="selectedOwnerId"
        :items="ownerOptions"
        item-title="title"
        item-value="value"
        :label="$t('ncrm.contacts.details.addToBoard.fields.owner')"
        :loading="ownersLoading"
        :no-data-text="$t('ncrm.contacts.details.addToBoard.empty.owners')"
        :menu-props="selectMenuProps"
        hide-details="auto"
      />

      <v-alert v-if="errorMessage" type="error" density="compact">
        {{ errorMessage }}
      </v-alert>
    </div>
  </NcrmRecordEditDialog>
</template>

<script>
import axios from "axios";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import { resolveCurrentUserId } from "@/Utile/ncrmCurrentUser.mjs";

const VALID_LEAD_SCOPES = new Set(["company_only", "both"]);

export default {
  name: "NcrmAddToBoardDrawer",
  components: { NcrmRecordEditDialog },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => value === "lead" || value === "deal",
    },
    companyId: {
      type: [Number, String],
      default: null,
    },
    companyName: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue", "added", "error"],
  data() {
    return {
      boards: [],
      boardsLoading: false,
      stages: [],
      stagesLoading: false,
      owners: [],
      ownersLoading: false,
      selectedBoardId: null,
      selectedStageId: null,
      selectedOwnerId: null,
      dealName: "",
      submitting: false,
      errorMessage: "",
    };
  },
  computed: {
    resolvedTitle() {
      return this.mode === "deal"
        ? this.$t("ncrm.contacts.details.addToBoard.titleDeal")
        : this.$t("ncrm.contacts.details.addToBoard.titleLead");
    },
    resolvedSubtitle() {
      const name = String(this.companyName || "").trim();
      if (!name) return "";
      return this.mode === "deal"
        ? this.$t("ncrm.contacts.details.addToBoard.subtitleDeal", { company: name })
        : this.$t("ncrm.contacts.details.addToBoard.subtitleLead", { company: name });
    },
    resolvedHeaderIcon() {
      return this.mode === "deal" ? "mdi-handshake-outline" : "mdi-account-multiple-plus-outline";
    },
    boardOptions() {
      return this.boards.map((board) => ({
        title: String(board?.name || `#${board?.id || "-"}`),
        value: Number(board?.id || 0),
      }));
    },
    stageOptions() {
      return this.stages.map((stage) => ({
        title: String(stage?.name || `#${stage?.id || "-"}`),
        value: Number(stage?.id || 0),
      }));
    },
    ownerOptions() {
      return this.owners.map((owner) => ({
        title: String(owner?.display_name || owner?.name || owner?.email || `#${owner?.id || "-"}`),
        value: Number(owner?.id || 0),
      }));
    },
    selectMenuProps() {
      return {
        maxHeight: 320,
        contentClass: "ncrm-add-to-board-select-menu",
        zIndex: 2710,
      };
    },
    canSubmit() {
      const baseValid = Boolean(
        this.selectedBoardId && this.selectedStageId && this.companyId && this.selectedOwnerId && !this.submitting
      );
      if (!baseValid) return false;
      if (this.mode === "deal") {
        return Boolean(String(this.dealName || "").trim());
      }
      return true;
    },
  },
  watch: {
    modelValue(value) {
      if (value) {
        this.openDrawer();
      } else {
        this.resetState();
      }
    },
    selectedBoardId(value, previous) {
      if (value === previous) return;
      this.selectedStageId = null;
      this.stages = [];
      if (!value) return;
      this.fetchStages();
    },
  },
  methods: {
    handleModelUpdate(value) {
      this.$emit("update:modelValue", Boolean(value));
    },
    resetState() {
      this.boards = [];
      this.stages = [];
      this.owners = [];
      this.selectedBoardId = null;
      this.selectedStageId = null;
      this.selectedOwnerId = null;
      this.dealName = "";
      this.submitting = false;
      this.errorMessage = "";
    },
    async openDrawer() {
      this.errorMessage = "";
      if (this.mode === "deal") {
        const trimmedName = String(this.companyName || "").trim();
        this.dealName = trimmedName
          ? this.$t("ncrm.contacts.details.addToBoard.defaultDealName", { company: trimmedName })
          : "";
      }
      await Promise.allSettled([this.fetchBoards(), this.fetchOwners()]);
    },
    async fetchBoards() {
      this.boardsLoading = true;
      this.errorMessage = "";
      try {
        if (this.mode === "lead") {
          const { data } = await axios.get("/ncrm/leads/boards");
          const items = Array.isArray(data?.items) ? data.items : [];
          this.boards = items.filter((board) => {
            if (Number(board?.is_active) !== 1 && board?.is_active !== true) return false;
            const scope = String(board?.entity_scope || "both").trim().toLowerCase();
            return VALID_LEAD_SCOPES.has(scope);
          });
        } else {
          const { data } = await axios.get("/ncrm/deals/pipelines");
          const items = Array.isArray(data?.items) ? data.items : [];
          this.boards = items.filter((board) => Number(board?.is_active) !== 0);
        }
      } catch (error) {
        this.boards = [];
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.contacts.details.addToBoard.messages.loadBoardsError");
      } finally {
        this.boardsLoading = false;
      }
    },
    async fetchStages() {
      if (!this.selectedBoardId) return;
      this.stagesLoading = true;
      this.errorMessage = "";
      try {
        const url =
          this.mode === "lead"
            ? `/ncrm/leads/boards/${this.selectedBoardId}/stages`
            : `/ncrm/deals/pipelines/${this.selectedBoardId}/stages`;
        const { data } = await axios.get(url);
        const items = Array.isArray(data?.items) ? data.items : [];
        this.stages = items.filter((stage) => Number(stage?.is_active) !== 0);
        const firstActive = this.stages[0];
        if (firstActive?.id) {
          this.selectedStageId = Number(firstActive.id);
        }
      } catch (error) {
        this.stages = [];
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.contacts.details.addToBoard.messages.loadStagesError");
      } finally {
        this.stagesLoading = false;
      }
    },
    async fetchOwners() {
      this.ownersLoading = true;
      try {
        const url = this.mode === "lead" ? "/ncrm/leads/owners" : "/ncrm/deals/owners";
        const { data } = await axios.get(url);
        const items = Array.isArray(data?.items) ? data.items : [];
        this.owners = items;
        const currentUserId = await resolveCurrentUserId(axios);
        if (currentUserId && items.some((owner) => Number(owner?.id) === Number(currentUserId))) {
          this.selectedOwnerId = Number(currentUserId);
        } else if (items[0]?.id) {
          this.selectedOwnerId = Number(items[0].id);
        }
      } catch (error) {
        this.owners = [];
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.contacts.details.addToBoard.messages.loadOwnersError");
      } finally {
        this.ownersLoading = false;
      }
    },
    async submitSelection() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.errorMessage = "";
      try {
        if (this.mode === "lead") {
          await this.submitLeadEntry();
        } else {
          await this.submitDeal();
        }
        this.$emit("added", { mode: this.mode });
        this.$emit("update:modelValue", false);
      } catch (error) {
        const code = error?.response?.data?.code;
        if (this.mode === "lead" && code === "LEAD_ENTRY_ALREADY_EXISTS") {
          this.errorMessage = this.$t("ncrm.contacts.details.addToBoard.messages.leadAlreadyExists");
        } else {
          this.errorMessage =
            error?.response?.data?.error || this.$t("ncrm.contacts.details.addToBoard.messages.submitError");
        }
        this.$emit("error", this.errorMessage);
      } finally {
        this.submitting = false;
      }
    },
    async submitLeadEntry() {
      const payload = {
        entries: [
          {
            entity_type: "company",
            ncrm_company_id: Number(this.companyId),
            stage_id: Number(this.selectedStageId),
            owner_user_id: Number(this.selectedOwnerId),
            status: "open",
          },
        ],
      };
      await axios.post(`/ncrm/leads/boards/${this.selectedBoardId}/entries/bulk-add`, payload);
    },
    async submitDeal() {
      const payload = {
        name: String(this.dealName || "").trim(),
        owner_user_id: Number(this.selectedOwnerId),
        pipeline_id: Number(this.selectedBoardId),
        stage_id: Number(this.selectedStageId),
        ncrm_company_id: Number(this.companyId),
      };
      await axios.post("/ncrm/deals", payload);
    },
  },
};
</script>
